import React, { useContext, useState, forwardRef } from 'react';
import { UserContext } from '../../../../lib/contexts/UserContext';
import { Box, Button, Typography, Select, MenuItem, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useIntercom } from 'react-use-intercom';

const ReportOrderIssueCard = forwardRef(({ onClose, orderID }, ref) => {
  const { boot, showMessages } = useIntercom();
  const {
    state: { userObject },
  } = useContext(UserContext);
  const [issue, setIssue] = React.useState('');
  const [comments, setComments] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [replacementRequested, setReplacementRequested] = useState(false);
  const [waitForNextOrder, setWaitForNextOrder] = useState(false);

  const handleChange = (event) => {
    setIssue(event.target.value);
  };

  const handleCommentsChange = (event) => {
    setComments(event.target.value);
  };

  const handleReplacementClick = () => {
    setReplacementRequested(true);
    setWaitForNextOrder(false);
    closeAndOpenIntercom(true);
  };

  const handleWaitClick = () => {
    setWaitForNextOrder(true);
    setReplacementRequested(false);
    closeAndOpenIntercom(false);
  };

  const closeAndOpenIntercom = (isReplacementRequested) => {
    onClose();
    setTimeout(() => {
      openIntercomWithMessage(isReplacementRequested);
    }, 300);
  };

  const openIntercomWithMessage = (isReplacementRequested) => {
    boot();
    const selectedOption = reportOrderOptions.find((option) => option.value === issue);
    let message = issue === 'something-else' ? comments : selectedOption?.message || 'I have an issue to report.';

    if (isReplacementRequested) {
      message += ' Could you please send a replacement?';
    }

    message += ` (Order ID is ${orderID})`;
    window.Intercom('showNewMessage', message);
  };

  const replacementRequestedMessage =
    'Your replacement order has been requested. Check back on your orders page, once the order is processed, you will be able to track it!';
  const waitForNextOrderMessage =
    "We've captured your order issue, and we are working to ensure it doesn't happen again!";
  const reportOrderOptions = [
    {
      value: 'never-arrived',
      label: 'My order never arrived',
      message: 'Hi, my order never arrived. Can you help me with this?',
    },
    {
      value: 'wrong-location',
      label: 'My order was delivered to the wrong location.',
      message: 'Hello, my order was delivered to the wrong location. What can be done about this?',
    },
    {
      value: 'wrong-box',
      label: 'I received the wrong box.',
      message: 'I received the wrong box in my order. Could you please assist me?',
    },
    {
      value: 'missing-items',
      label: 'My order is missing some items.',
      message: 'Some items are missing from my order. Can you help resolve this?',
    },
    {
      value: 'out-of-temperature',
      label: 'My order arrived out of temperature.',
      message: 'My order arrived out of temperature. What should I do next?',
    },
    {
      value: 'something-else',
      label: "It's something else.",
      message: 'I have a different issue. Here are the details: ',
    },
  ];

  const handleSubmitPopup = () => {
    return (
      <Box
        id="submit-popup-container"
        sx={{
          display: 'flex',
          width: '377px',
          padding: 'var(--5, 24px)',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'var(--5, 24px)',
          borderRadius: '10px',
          border: '1px solid var(--color6, #D6D2E7)',
          background: 'var(--color2, #FBFBFC)',
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
        }}
      >
        <Box
          id="submit-popup-label-container"
          sx={{
            display: 'flex',
            paddingBottom: 'var(--2, 7px)',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--2-5, 10px)',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="submit-title-container"
            sx={{
              display: 'flex',
              paddingTop: 'var(--1-5, 4px)',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 'var(--0, 0px)',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="submit-header-container"
              sx={{
                display: 'flex',
                padding: '7px 0px',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch',
              }}
            >
              <Typography
                sx={{
                  flex: '1 0 0',
                  color: 'var(--color12, #1C124D)',
                  fontFamily: 'Montserrat',
                  fontSize: '24px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%', // 30px
                }}
              >
                {replacementRequested
                  ? "We're On It!"
                  : waitForNextOrder
                  ? 'Got It!'
                  : reportOrderOptions.find((option) => option.value === issue)?.label || 'Order Issue'}
              </Typography>
              {/* Add header content here if needed */}
            </Box>
            <Button
              id="submit-popup-close-button"
              onClick={onClose}
              sx={{
                display: 'flex',
                width: 'var(--2, 28px)',
                height: 'var(--2, 28px)',
                padding: 'var(--0, 0px) var(--2, 7px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--2, 7px)',
                position: 'absolute',
                right: '-8px',
                top: '-8px',
                borderRadius: '999px',
                background: 'var(--color4, #EBE8F3)',
              }}
            >
              <CloseIcon
                sx={{
                  width: 'var(--_16, 16px)',
                  height: 'var(--_16, 16px)',
                  flexShrink: 0,
                }}
              />
            </Button>
          </Box>
          <Box
            id="submit-message-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--color12, #1C124D)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              {replacementRequested
                ? replacementRequestedMessage
                : waitForNextOrder
                ? waitForNextOrderMessage
                : reportOrderOptions.find((option) => option.value === issue)?.message || 'Please describe your issue.'}
            </Typography>
          </Box>
        </Box>
        {replacementRequested || waitForNextOrder ? (
          <Box
            id="finish-report-buttons-container"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              gap: 'var(--3-5, 16px)',
              alignSelf: 'stretch',
            }}
          >
            <Button
              id="back-button"
              sx={{
                display: 'flex',
                height: 'var(--4, 44px)',
                padding: 'var(--0, 0px) var(--4, 18px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--4, 18px)',
                flex: '1 0 0',
                borderRadius: 'var(--4, 9px)',
                border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  textAlign: 'center',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Go Back
              </Typography>
            </Button>
            <Button
              id="done-button"
              sx={{
                display: 'flex',
                height: 'var(--4, 44px)',
                padding: 'var(--0, 0px) var(--4, 18px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--4, 18px)',
                flex: '1 0 0',
                borderRadius: 'var(--4, 9px)',
                border: '1px solid var(--color12, #1C124D)',
                background: 'var(--color12, #1C124D)',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--color1, #FDFDFD)',
                  textAlign: 'center',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Done
              </Typography>
            </Button>
          </Box>
        ) : (
          <Box
            id="choose-resolution-buttons-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              gap: 'var(--3-5, 16px)',
              alignSelf: 'stretch',
            }}
          >
            <Button
              id="send-replacement-button-container"
              onClick={handleReplacementClick}
              sx={{
                display: 'flex',
                height: 'var(--4, 44px)',
                padding: 'var(--0, 0px) var(--4, 18px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--4, 18px)',
                alignSelf: 'stretch',
                borderRadius: 'var(--4, 9px)',
                border: '1px solid var(--color12, #1C124D)',
                background: 'var(--color12, #1C124D)',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--color1, #FDFDFD)',
                  textAlign: 'center',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Yes, send a replacement.
              </Typography>
            </Button>
            <Button
              id="wait-for-next-order-button-container"
              onClick={handleWaitClick}
              sx={{
                display: 'flex',
                height: 'var(--4, 44px)',
                padding: 'var(--0, 0px) var(--4, 18px)',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--4, 18px)',
                alignSelf: 'stretch',
                borderRadius: 'var(--4, 9px)',
                border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--color12, #1C124D)',
                  textAlign: 'center',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                No, I'll wait for my next order.
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const handleSubmit = () => {
    setShowPopup(true);
  };

  return (
    <Box
      ref={ref}
      id="report-issue-card-container"
      sx={{
        display: 'flex',
        width: '377px',
        padding: 'var(--5, 24px)',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--5, 24px)',
        borderRadius: '10px',
        border: '1px solid var(--color6, #D6D2E7)',
        background: 'var(--color2, #FBFBFC)',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflowY: 'auto',
        maxHeight: '90vh',
      }}
    >
      {showPopup ? (
        handleSubmitPopup()
      ) : (
        <>
          <Box
            id="overall-report-order-issue-container"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              width: '461px',
              padding: 'var(--5, 24px)',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 'var(--5, 24px)',
              borderRadius: '10px',
              border: '1px solid var(--color6, #D6D2E7)',
              background: 'var(--color2, #FBFBFC)',
            }}
          >
            <Box
              id="report-label-container"
              sx={{
                display: 'flex',
                paddingBottom: 'var(--2, 7px)',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 'var(--2-5, 10px)',
                alignSelf: 'stretch',
              }}
            >
              <Box
                id="report-label-title-container"
                sx={{
                  display: 'flex',
                  paddingTop: 'var(--1-5, 4px)',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 'var(--0, 0px)',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  id="header"
                  sx={{
                    display: 'flex',
                    padding: '7px 0px',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch',
                  }}
                >
                  <Typography
                    sx={{
                      flex: '1 0 0',
                      color: 'var(--color12, #1C124D)',
                      fontFamily: 'Montserrat',
                      fontSize: '24px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '125%', // 30px
                    }}
                  >
                    Report an Order Issue
                  </Typography>
                </Box>
                <Button
                  id="close-button"
                  onClick={onClose}
                  sx={{
                    display: 'flex',
                    width: 'var(--2, 28px)',
                    height: 'var(--2, 28px)',
                    padding: 'var(--0, 0px) var(--2, 7px)',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 'var(--2, 7px)',
                    position: 'absolute',
                    right: '-8px',
                    top: '-8px',
                    borderRadius: '999px',
                    background: 'var(--color4, #EBE8F3)',
                  }}
                >
                  <CloseIcon
                    sx={{
                      width: 'var(--_16, 16px)',
                      height: 'var(--_16, 16px)',
                      flexShrink: 0,
                    }}
                  />
                </Button>
              </Box>
              <Box
                id="report-label-message"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '24px',
                  alignSelf: 'stretch',
                }}
              >
                <Typography
                  sx={{
                    alignSelf: 'stretch',
                    color: 'var(--color12, #1C124D)',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                  }}
                >
                  We're sorry to hear you had an issue with your order! Please tell us what happened, and we'll make it
                  right.
                </Typography>
              </Box>
            </Box>
            <Box
              id="report-order-dropdown-container"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: 'var(--5, 24px)',
                alignSelf: 'stretch',
              }}
            >
              <Box
                id="dropdown-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '12px',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  id="dropdown-label-container"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'var(--color12, #1C124D)',
                      textAlign: 'right',
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    Order Issue
                  </Typography>
                </Box>
                <Select
                  value={issue}
                  onChange={handleChange}
                  displayEmpty
                  sx={{
                    display: 'flex',
                    height: 'var(--4, 44px)',
                    padding: 'var(--4, 18px) var(--3-5, 16px)',
                    alignItems: 'center',
                    gap: 'var(--4, 18px)',
                    alignSelf: 'stretch',
                    borderRadius: 'var(--4, 9px)',
                    border: 'var(--0-5, 1px) solid var(--color8, #A69ECA)',
                    background: 'var(--color1, #FDFDFD)',
                  }}
                >
                  <MenuItem
                    value=""
                    disabled
                  >
                    <Typography
                      sx={{
                        flex: '1 0 0',
                        color: 'var(--color12, #1C124D)',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                      }}
                    >
                      Select
                    </Typography>
                  </MenuItem>
                  {reportOrderOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      <Typography
                        sx={{
                          color: 'var(--color12, #1C124D)',
                          leadingTrim: 'both',
                          textEdge: 'cap',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                        }}
                      >
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {issue === 'something-else' && (
                <Box
                  id="comments-box"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '16px',
                    alignSelf: 'stretch',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'var(--color12, #1C124D)',
                      textAlign: 'right',
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    Comments
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    value={comments}
                    onChange={handleCommentsChange}
                    sx={{
                      background: 'var(--color1, #FDFDFD)',
                      borderRadius: 'var(--4, 9px)',
                      border: '1px solid var(--color8, #A69ECA)',
                    }}
                  />
                </Box>
              )}
              <Button
                id="submit-button"
                onClick={handleSubmit}
                sx={{
                  display: 'flex',
                  width: '225px',
                  height: 'var(--4, 44px)',
                  padding: 'var(--0, 0px) var(--4, 18px)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--4, 18px)',
                  borderRadius: 'var(--4, 9px)',
                  border: '1px solid var(--color12, #1C124D)',
                  background: 'var(--color12, #1C124D)',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color1, #FDFDFD)',
                    textAlign: 'center',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Submit
                </Typography>
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

ReportOrderIssueCard.displayName = 'ReportOrderIssueCard';

export default ReportOrderIssueCard;
